import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiGetMessagesResponseSuccess,
    type OpenApiGetMessagesSearchParams,
    OpenApiGetMessagesUrl,
    type OpenApiGetUnreadMessagesCountResponseSuccess,
    OpenApiGetUnreadMessagesCountUrl,
    type OpenApiMarkAllMessagesAsReadResponseSuccess,
    OpenApiMarkAllMessagesAsReadUrl,
    type OpenApiMarkMessagesAsReadRequest,
    type OpenApiMarkMessagesAsReadResponseSuccess,
    OpenApiMarkMessagesAsReadUrl,
} from '#shared/api/schema';

const DashboardMessagesTag = 'DashboardMessages' as const;

export const DASHBOARD_MESSAGES_QUERY_ARGS = { per_page: 500, page: 1 } satisfies OpenApiGetMessagesSearchParams;

const dashboardMessagesListTag = { type: DashboardMessagesTag, id: 'dashboardMessagesList' };
const dashboardMessagesUnreadCountTag = { type: DashboardMessagesTag, id: 'dashboardMessagesUnreadCount' };

const api = createApi({
    name: 'entities/dashboardMessage/api/index',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [DashboardMessagesTag],
    endpoints: (builder) => {
        return {
            getMessages: builder.query<OpenApiGetMessagesResponseSuccess, OpenApiGetMessagesSearchParams>({
                query: (params) => {
                    return {
                        url: OpenApiGetMessagesUrl,
                        method: 'GET',
                        params: params,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        dashboardMessagesListTag,
                        ...result.messages.map((message) => {
                            return { type: DashboardMessagesTag, id: message.message_id };
                        }),
                    ];
                },
                keepUnusedDataFor: 0,
            }),
            getUnreadCount: builder.query<OpenApiGetUnreadMessagesCountResponseSuccess, void>({
                query: () => {
                    return {
                        url: OpenApiGetUnreadMessagesCountUrl,
                        method: 'GET',
                    };
                },
                providesTags: [dashboardMessagesUnreadCountTag],
                keepUnusedDataFor: 0,
            }),
            markAllAsRead: builder.mutation<OpenApiMarkAllMessagesAsReadResponseSuccess, void>({
                query: () => {
                    return {
                        url: OpenApiMarkAllMessagesAsReadUrl,
                        method: 'POST',
                    };
                },
                invalidatesTags: [dashboardMessagesListTag, dashboardMessagesUnreadCountTag],
                async onQueryStarted(_arg, { queryFulfilled }) {
                    const messagesPatch = api.endpoints.getMessages.updateQueryData(
                        DASHBOARD_MESSAGES_QUERY_ARGS,
                        (draft) => {
                            draft.messages.forEach((message) => {
                                if (!message.read_at) {
                                    message.read_at = new Date().toISOString();
                                }
                            });
                        }
                    );
                    const unreadCountPatch = api.endpoints.getUnreadCount.updateQueryData(undefined, (draft) => {
                        draft.unread_count = 0;
                    });

                    try {
                        await queryFulfilled;
                    } catch {
                        messagesPatch.undo();
                        unreadCountPatch.undo();
                    }
                },
            }),
            markAsRead: builder.mutation<OpenApiMarkMessagesAsReadResponseSuccess, OpenApiMarkMessagesAsReadRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiMarkMessagesAsReadUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error, args) => {
                    if (error) {
                        return [];
                    }

                    return [
                        dashboardMessagesUnreadCountTag,
                        ...args.message_ids.map((id) => {
                            return { type: DashboardMessagesTag, id };
                        }),
                    ];
                },
                async onQueryStarted({ message_ids }, { queryFulfilled }) {
                    const ids = new Set(message_ids);
                    let markedCount = 0;

                    const messagesPatch = api.endpoints.getMessages.updateQueryData(
                        DASHBOARD_MESSAGES_QUERY_ARGS,
                        (draft) => {
                            draft.messages.forEach((message) => {
                                if (ids.has(message.message_id) && !message.read_at) {
                                    message.read_at = new Date().toISOString();
                                    markedCount += 1;
                                }
                            });
                        }
                    );
                    const unreadCountPatch = api.endpoints.getUnreadCount.updateQueryData(undefined, (draft) => {
                        draft.unread_count = Math.max(0, draft.unread_count - markedCount);
                    });

                    try {
                        await queryFulfilled;
                    } catch {
                        messagesPatch.undo();
                        unreadCountPatch.undo();
                    }
                },
            }),
        };
    },
});

export const useDashboardMessages = api.useGetMessagesQuery;
export const useDashboardMessagesUnreadCount = api.useGetUnreadCountQuery;
export const useDashboardMessagesUnreadCountLazy = api.useLazyGetUnreadCountQuery;
export const markDashboardMessageAsRead = api.endpoints.markAsRead.mutate;
export const markAllDashboardMessagesAsRead = api.endpoints.markAllAsRead.mutate;
